import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordPage() {
    const { user, applyLoginResponse } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const forced = !!user?.must_change_password;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (next.length < 10) {
            setError("New password must be at least 10 characters");
            return;
        }
        if (next !== confirm) {
            setError("New password and confirmation don't match");
            return;
        }
        if (next === current) {
            setError("New password must be different from the current one");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            applyLoginResponse(data);
            toast.success("Password updated", { position: "bottom-center" });
            const redirect = loc.state?.from?.pathname || "/";
            nav(redirect, { replace: true });
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
        setBusy(false);
    };

    return (
        <div
            data-testid="change-password-page"
            className="min-h-[calc(100vh-62px)] flex items-center justify-center px-4 py-10"
        >
            <div className="tms-card p-8 w-full max-w-md rise-in">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={18} style={{ color: "var(--tms-green-dark)" }} />
                    <h1 style={{ fontSize: 20, fontWeight: 600, color: "var(--tms-navy)" }}>
                        {forced ? "Set a new password to continue" : "Change your password"}
                    </h1>
                </div>
                <p style={{ fontSize: 13, color: "var(--tms-muted)", marginBottom: 20 }}>
                    {forced
                        ? "This account uses a temporary password. Please set a new one before using the library."
                        : "Pick a strong password of at least 10 characters."}
                </p>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tms-muted)" }}>
                            Current password
                        </label>
                        <input
                            type="password"
                            className="tms-input"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            autoComplete="current-password"
                            required
                            data-testid="change-current-password-input"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tms-muted)" }}>
                            New password (min 10 characters)
                        </label>
                        <input
                            type="password"
                            className="tms-input"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            minLength={10}
                            autoComplete="new-password"
                            required
                            data-testid="change-new-password-input"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tms-muted)" }}>
                            Confirm new password
                        </label>
                        <input
                            type="password"
                            className="tms-input"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            minLength={10}
                            autoComplete="new-password"
                            required
                            data-testid="change-confirm-password-input"
                        />
                    </div>
                    {error && (
                        <div
                            data-testid="change-password-error"
                            style={{ background: "#fdeceb", color: "var(--tms-danger)", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}
                        >
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-primary-green w-full justify-center"
                        style={{ padding: "12px 16px" }}
                        data-testid="change-password-submit-btn"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {busy ? "Updating…" : (
                            <>
                                <Lock size={14} /> Update password
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
